import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Cpu, ChevronRight,
  // Why - today's challenges
  AppWindow, Layers3, Smartphone as PhoneIcon, MonitorOff, ImageOff, UserX, WifiOff, Lock,
  // Why - with SmartRF
  LayoutTemplate, Workflow, Compass, Camera, MousePointerClick, Rocket, Tablet, Wifi,
  // Benefits
  TrendingUp, GraduationCap, Users, Target, RefreshCw, ShieldAlert, Zap,
  // Partnership
  Handshake,
} from "lucide-react";

const IMG_HERO = "/images/image-5.webp";
const IMG_COMPARE = "/images/image-4.webp";
const IMG_CAPABILITIES = "/images/smartrf-platform-capabilities.webp";

const HERO_BULLETS = [
  "SAP-connected mobility",
  "Online & offline execution",
  "Multi-device support",
  "Modern responsive RF UX",
];

const TODAY_PAINS = [
  { icon: AppWindow,   label: "Complex SAP ITS screens" },
  { icon: Layers3,     label: "Too many fields" },
  { icon: PhoneIcon,   label: "Poor mobile usability" },
  { icon: MonitorOff,  label: "Limited responsive behavior" },
  { icon: ImageOff,    label: "No image capture" },
  { icon: UserX,       label: "Difficult operator adoption" },
  { icon: WifiOff,     label: "Offline requirement" },
  { icon: Lock,        label: "Device limitations" },
];

const WITH_SMARTRF = [
  { icon: LayoutTemplate,    label: "Responsive warehouse UX" },
  { icon: Workflow,          label: "Simplified workflows" },
  { icon: Compass,           label: "Guided operator experience" },
  { icon: Camera,            label: "Image capture support" },
  { icon: MousePointerClick, label: "Reduced clicks" },
  { icon: Rocket,            label: "Faster adoption" },
  { icon: Tablet,            label: "Multi-device support" },
  { icon: Wifi,              label: "SAP-connected real-time execution" },
];

const BENEFITS = [
  { icon: Rocket,        label: "Faster operator onboarding",   desc: "Intuitive screens and guided workflows help new users get productive quickly." },
  { icon: TrendingUp,    label: "Improved warehouse productivity", desc: "Streamlined processes and fewer steps drive higher throughput every day." },
  { icon: GraduationCap, label: "Reduced RF training effort",    desc: "Simplified user experience reduces training time and ongoing support needs." },
  { icon: Users,         label: "Better operator adoption",      desc: "Clean, responsive RF experience drives higher engagement and user satisfaction." },
  { icon: Target,        label: "Improved execution accuracy",   desc: "Real-time validation and accurate data capture minimize errors and exceptions." },
  { icon: RefreshCw,     label: "Modernized SAP RF experience",  desc: "A modern, responsive interface improves efficiency across all warehouse tasks." },
  { icon: ShieldAlert,   label: "Reduced operational downtime",  desc: "Offline capability and real-time sync keep operations running without interruptions." },
  { icon: Zap,           label: "Faster warehouse transactions", desc: "Optimized data capture and workflow automation speed up every transaction." },
];

const APPROACH = [
  { num: "1", title: "Rapid Deployment",       desc: "Simple configuration and minimal training to get warehouse teams live quickly." },
  { num: "2", title: "SAP-Connected Mobility", desc: "Clean-core aligned deployment that integrates directly with existing SAP processes." },
  { num: "3", title: "Configurable UX Rollout", desc: "Extensible framework that adapts to your warehouse workflows site by site." },
];

const PARTNERSHIP_BULLETS = [
  "SAP warehouse mobility modernization",
  "RF transformation services",
  "Warehouse execution expertise",
  "UX-led warehouse adoption",
  "SAP-connected operational mobility",
];

// --- Reusable components (page-local) -------------------------------------
function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="max-w-3xl mx-auto text-center mb-10">
      {eyebrow && (
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#025979] mb-3">
          {eyebrow}
        </p>
      )}
      <h2
        className="text-2xl sm:text-3xl font-bold text-[#111827] tracking-tight"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {title}
      </h2>
      {subtitle && (
        <p className="mt-3 text-sm sm:text-base text-[#6B7280] leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function PainCard({ icon: Icon, label, accent = "muted" }) {
  const isBrand = accent === "brand";
  return (
    <div
      className={`flex items-start gap-3 p-3.5 rounded-sm border bg-white ${
        isBrand ? "border-[#025979]/30" : "border-[#E5E7EB]"
      }`}
    >
      <div
        className={`w-8 h-8 shrink-0 rounded-sm flex items-center justify-center ${
          isBrand ? "bg-[#E0F2FE] text-[#025979]" : "bg-[#FEF2F2] text-[#B91C1C]"
        }`}
      >
        <Icon className="w-4 h-4" strokeWidth={1.75} />
      </div>
      <p className="text-[13px] leading-snug text-[#111827] font-medium">{label}</p>
    </div>
  );
}

function openRequestDemo() {
  if (typeof window === "undefined") return;
  // The global modal already auto-detects "smartrf" from the pathname,
  // but we also pass the detail explicitly so deep-linked use works
  // even if the path detection ever changes.
  window.dispatchEvent(new CustomEvent("requestDemo:open", { detail: { solution: "smartrf" } }));
}

// --- Page ------------------------------------------------------------------
export default function SmartRFPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1">

        {/* SECTION 1 — HERO ----------------------------------------------- */}
        <section className="bg-[#F8FAFC] border-b border-[#E5E7EB]" data-testid="smartrf-hero">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <div className="inline-flex items-center gap-1.5 bg-white border border-[#E5E7EB] rounded-sm px-2.5 py-1 mb-4">
                  <Cpu className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
                  <span className="text-[11px] font-medium text-[#025979] tracking-wide">
                    WarehouseX SmartExecution
                  </span>
                </div>
                <h1
                  className="text-4xl sm:text-5xl font-bold tracking-tight text-[#111827] leading-[1.05]"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                  data-testid="smartrf-hero-title"
                >
                  SmartRF
                </h1>
                <p className="mt-4 text-base sm:text-lg text-[#374151] font-medium leading-relaxed max-w-lg">
                  Modern SAP Warehouse Mobility &amp; Execution Platform
                </p>
                <p className="mt-3 text-sm sm:text-base text-[#6B7280] leading-relaxed max-w-lg">
                  Accelerate warehouse operations with intuitive RF workflows, offline execution support, and modern SAP-connected mobility.
                </p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4 max-w-lg">
                  {HERO_BULLETS.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-[#111827]">
                      <span className="inline-flex w-4 h-4 rounded-sm bg-[#E0F2FE] text-[#025979] items-center justify-center mt-0.5">
                        <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 text-[12px] text-[#6B7280] leading-relaxed max-w-lg italic">
                  Powered by PreBilt technology and implemented by PrimeS4 as part of the WarehouseX SmartExecution portfolio.
                </p>
                <div className="mt-7">
                  <Button
                    onClick={openRequestDemo}
                    data-testid="smartrf-hero-request-demo"
                    className="rounded-sm h-11 px-6 text-sm font-semibold bg-[#025979] hover:bg-[#03738C] text-white"
                  >
                    Request Demo
                  </Button>
                </div>
              </div>
              <div>
                <div className="border border-[#E5E7EB] rounded-sm overflow-hidden shadow-sm bg-white">
                  <img loading="lazy"
                    src={IMG_HERO}
                    alt="SmartRF — Modern SAP Warehouse Mobility & Execution Platform"
                    className="w-full h-auto object-contain"
                    data-testid="smartrf-hero-image"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 — WHY SMARTRF IS NEEDED ------------------------------ */}
        <section className="py-20 md:py-28 bg-white" data-testid="smartrf-why">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="The RF Gap"
              title="Why SmartRF is Needed"
              subtitle="Modern warehouse operations require faster, simpler, and more responsive SAP mobility experiences."
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[#B91C1C] mb-3">
                  Today's RF Challenges
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {TODAY_PAINS.map((p) => (
                    <PainCard key={p.label} {...p} accent="muted" />
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[#025979] mb-3">
                  With SmartRF
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {WITH_SMARTRF.map((p) => (
                    <PainCard key={p.label} {...p} accent="brand" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3 — ITS MOBILE VS SMARTRF (image only) ------------------ */}
        <section className="py-20 md:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]" data-testid="smartrf-compare">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Comparison"
              title="ITS Mobile vs SmartRF"
              subtitle="A modern RF experience that drives productivity, accuracy, and adoption — side by side with legacy SAP ITS Mobile."
            />
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
              <img
                src={IMG_COMPARE}
                alt="ITS Mobile vs SmartRF — Modern RF Experience that Drives Productivity"
                className="w-full h-auto"
                data-testid="smartrf-compare-image"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* SECTION 4 — SMARTRF PLATFORM CAPABILITIES (image-led) ---------- */}
        <section className="py-20 md:py-28 bg-white" data-testid="smartrf-capabilities">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-10">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#025979] mb-3">
                Capabilities
              </p>
              <p className="text-sm sm:text-base text-[#6B7280] leading-relaxed">
                A unified platform view of SmartRF — execution, mobility, scanning, analytics, and AI in one operator-first experience.
              </p>
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden">
              <img
                src={IMG_CAPABILITIES}
                alt="SmartRF Platform Capabilities — Modern mobility, smarter execution, real results"
                className="w-full h-auto"
                data-testid="smartrf-capabilities-image"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* SECTION 5 — BUSINESS BENEFITS (open, premium layout) ---------- */}
        <section className="py-20 md:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB]" data-testid="smartrf-benefits">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Outcomes"
              title="Business Benefits"
              subtitle="Improve warehouse execution, operator adoption, and operational efficiency."
            />
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-14 gap-x-10 max-w-6xl mx-auto">
              {BENEFITS.map((b, idx) => (
                <div
                  key={b.label}
                  data-testid={`smartrf-benefit-${idx}`}
                  className={`text-center px-2 ${
                    idx >= 4 ? "lg:pt-12 lg:border-t lg:border-[#E5E7EB]" : ""
                  }`}
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-[#E0F2FE] flex items-center justify-center mb-5">
                    <b.icon className="w-6 h-6 text-[#025979]" strokeWidth={1.75} />
                  </div>
                  <h3
                    className="text-base font-semibold text-[#111827] tracking-tight"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    {b.label}
                  </h3>
                  <p className="mt-2.5 text-[13px] text-[#6B7280] leading-relaxed max-w-[18rem] mx-auto">
                    {b.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 6 — IMPLEMENTATION APPROACH (compact) ------------------ */}
        <section className="py-16 md:py-20 bg-white" data-testid="smartrf-approach">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              eyebrow="Delivery"
              title="Implementation Approach"
              subtitle="Rapid deployment with minimal disruption to existing SAP operations."
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto">
              {APPROACH.map((a) => (
                <Card
                  key={a.num}
                  className="bg-white border border-[#E5E7EB] hover:border-[#025979]/40 transition-colors rounded-sm shadow-none"
                >
                  <CardContent className="p-5">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#025979] mb-2">
                      Step {a.num}
                    </div>
                    <h3
                      className="text-base font-semibold text-[#111827]"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                    >
                      {a.title}
                    </h3>
                    <p className="mt-1.5 text-[13px] text-[#6B7280] leading-relaxed">{a.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 7 — PARTNERSHIP ---------------------------------------- */}
        <section className="py-14 md:py-16 bg-[#F8FAFC] border-y border-[#E5E7EB]" data-testid="smartrf-partnership">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-7 md:p-9">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-sm bg-[#E0F2FE] flex items-center justify-center">
                  <Handshake className="w-5 h-5 text-[#025979]" strokeWidth={1.5} />
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#025979]">
                  Partnership
                </p>
              </div>
              <h2
                className="text-2xl sm:text-3xl font-bold text-[#111827] tracking-tight"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                PrimeS4 + PreBilt Partnership
              </h2>
              <p className="mt-3 text-sm sm:text-base text-[#6B7280] leading-relaxed">
                PrimeS4 delivers SmartRF as part of the WarehouseX SmartExecution portfolio in partnership with PreBilt mobility technology.
              </p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-6">
                {PARTNERSHIP_BULLETS.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-[#111827]">
                    <span className="inline-flex w-5 h-5 rounded-sm bg-[#E0F2FE] text-[#025979] items-center justify-center mt-0.5 shrink-0">
                      <ChevronRight className="w-3 h-3" strokeWidth={2.5} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 8 — FINAL CTA ------------------------------------------ */}
        <section className="bg-[#025979]" data-testid="smartrf-final-cta">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 text-center">
            <h2
              className="text-3xl sm:text-4xl font-bold text-white tracking-tight"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              Modernize Your SAP Warehouse Mobility
            </h2>
            <p className="mt-4 text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
              Deploy SmartRF to simplify warehouse execution, improve operator adoption, and accelerate SAP-connected mobility operations.
            </p>
            <div className="mt-8">
              <Button
                onClick={openRequestDemo}
                data-testid="smartrf-final-request-demo"
                className="rounded-sm h-11 px-7 text-sm font-semibold bg-white text-[#025979] hover:bg-[#E0F2FE]"
              >
                Request Demo
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
